import { SEA_LEVEL, WORLD_HEIGHT } from "../sim/world/constants";
import { WORLDGEN_BLOCK_ID } from "./blocks";
import { voxels } from "./constants";
import { hash32, hashFloat, hashSigned } from "./random";
import { erodedSurfaceHeight } from "./terrain";
import type { BiomeId, RegionGraph } from "./types";

/**
 * Vegetation is procedural, not an authored prefab list.
 *
 * At eight voxels per metre a single tree is ~150 000 voxels and a hectare of
 * grass is millions; storing that as `StructureBlock[]` would cost tens of
 * megabytes in every worker. So trees, blades and flowers are *sampled* per
 * sub-chunk from a deterministic grid, exactly the way caves are, and cost no
 * resident memory at all.
 *
 * Sizes come straight from docs/ART-DIRECTION.md §0: a trunk is 12–16 voxels
 * across, a grass blade 1–2 voxels wide and 4–12 tall.
 */

/**
 * One candidate tree per 7 m cell; density then thins them per biome.
 *
 * Crowns are 5-9 m across, so this puts them just about touching — a closed
 * canopy with gaps in it, which is what the references show. At 4 m spacing
 * they overlapped several deep and the whole world became one solid green
 * mass with no ground, no sky and nowhere to stand; at 9 m they stood alone in
 * a meadow and no shadow ever landed on anything.
 */
const TREE_CELL = voxels(7);
/**
 * Trunk half-width in voxels: 2-3, so a trunk reads 4-6 voxels across, i.e.
 * 50-75 cm.
 *
 * Was 6-8, which at eight voxels to the metre is a trunk one and a half to two
 * metres thick — a redwood holding up an oak's crown, and it rendered as a
 * pillar. The art-direction note that asked for "12-16 voxels across" was
 * written against a different voxel size and never rechecked.
 */
const TRUNK_RADIUS_MIN = 2;
const TRUNK_RADIUS_SPAN = 1;
const TRUNK_HEIGHT_MIN = voxels(4.5);
const TRUNK_HEIGHT_SPAN = voxels(4);
const CANOPY_RADIUS_MIN = voxels(2.6);
const CANOPY_RADIUS_SPAN = voxels(1.8);
/**
 * How far out a crown lobe's *centre* sits, and how big it is, as fractions of
 * the nominal canopy radius.
 *
 * These were 0.45-0.95 and 0.50-0.82, which put the union of the lobes at 1.77
 * canopy radii — so a tree documented as a 5-9 m crown was really 9-15 m across,
 * and every consumer that sized a box by `canopyRadiusXZ` cut the overhang off
 * flat. Pulled in so the union lands near 1.2 radii: still a cluster rather than
 * a ball, and now close enough to the nominal size that the documented figure is
 * true.
 */
const LOBE_OFFSET_MIN = 0.34;
const LOBE_OFFSET_SPAN = 0.28;
const LOBE_RADIUS_MIN = 0.42;
const LOBE_RADIUS_SPAN = 0.2;

/**
 * Widest any crown can reach from its trunk axis, used to size neighbourhood
 * searches. Individual trees carry their own exact `reach`; this is only the
 * static bound over every tree the generator can produce.
 *
 * It has to account for the lobes and for the lean, which together dominate it.
 * The old value summed the canopy and trunk radii and came to 38 voxels against
 * a true worst case near 80, so a tree whose crown overhung a chunk by more than
 * 38 voxels was never even considered when that chunk was generated — crowns
 * ended at chunk borders in a flat vertical cut.
 */
const MAX_CANOPY_RADIUS = CANOPY_RADIUS_MIN + CANOPY_RADIUS_SPAN;
const MAX_TRUNK_HEIGHT = TRUNK_HEIGHT_MIN + TRUNK_HEIGHT_SPAN;
/** Horizontal drift of a trunk per voxel of rise. */
const MAX_LEAN = 0.16;
export const MAX_CANOPY_REACH = Math.ceil(
  (LOBE_OFFSET_MIN + LOBE_OFFSET_SPAN + LOBE_RADIUS_MIN + LOBE_RADIUS_SPAN) * MAX_CANOPY_RADIUS
  + MAX_LEAN * (MAX_TRUNK_HEIGHT + MAX_CANOPY_RADIUS),
) + 2;

/**
 * Blade height in voxels, so 2-8 is 25-100 cm at eight voxels to the metre.
 *
 * Measured against the player, not taken from the art-direction note that said
 * 4-12: at eight per metre that is half a metre to a metre and a half, i.e.
 * grass up to a standing adult's eyes, and the camera saw nothing but green
 * walls. In the reference the meadow comes to roughly knee height, with the
 * ground and its shadows visible between the blades.
 */
const BLADE_HEIGHT_MIN = 2;
const BLADE_HEIGHT_SPAN = 6;
export const MAX_GROUND_COVER_HEIGHT = BLADE_HEIGHT_MIN + BLADE_HEIGHT_SPAN;

/** Vertical headroom above the smooth surface that vegetation can occupy. */
export const MAX_VEGETATION_HEIGHT = TRUNK_HEIGHT_MIN + TRUNK_HEIGHT_SPAN
  + 2 * (CANOPY_RADIUS_MIN + CANOPY_RADIUS_SPAN);

const TREE_DENSITY: Partial<Record<BiomeId, number>> = {
  forest: 0.6,
  "ancient-woods": 0.68,
  plains: 0.04,
  swamp: 0.3,
  marsh: 0.2,
  highlands: 0.15,
  tundra: 0.12,
};

/**
 * Fraction of columns *inside a grass patch* that carry a blade.
 *
 * This is not the coverage of the world: the patch field below gates whole areas
 * out first, and the two multiply. At 0.55 against the old patch gate the world
 * ended up with a blade on roughly 15% of its surface columns, which is what made
 * the meadow read as isolated spikes on bare ground with the dirt showing through
 * between them. In the references the grass closes over the ground nearly
 * everywhere and bare earth appears where something put it there — a path, a rock
 * face, a river bank.
 *
 * The earlier note here worried that a continuous carpet would merge into flat
 * slabs. That was true when blades shared a height and a colour; they no longer
 * do — `columnShade` gives each column its own green and the height jitter below
 * keeps neighbours from agreeing, and both are part of the greedy merge key, so a
 * closed field still meshes as blades.
 */
const GROUND_COVER_DENSITY: Partial<Record<BiomeId, number>> = {
  forest: 0.8,
  "ancient-woods": 0.84,
  plains: 0.75,
  swamp: 0.85,
  marsh: 0.85,
  highlands: 0.65,
  tundra: 0.42,
};

/**
 * Where the grass stops, and how wide the feathered rim of a patch is.
 *
 * The gate is what leaves bare ground; the ramp only softens the edge. Ramping
 * across the whole remaining range, as this did, meant the average column inside
 * a patch ran at *half* density and nowhere reached full — so there was no such
 * thing as thick grass anywhere in the world.
 */
const GROUND_COVER_PATCH_GATE = 0.25;
const GROUND_COVER_PATCH_RAMP = 0.25;

export interface TreeInstance {
  readonly x: number;
  readonly z: number;
  readonly groundY: number;
  readonly trunkRadius: number;
  readonly trunkHeight: number;
  readonly canopyRadiusXZ: number;
  readonly canopyRadiusY: number;
  readonly canopyCenterY: number;
  /**
   * Exact horizontal reach of this crown from the trunk axis, and its highest
   * voxel — the union of the central mass, every lobe and the lean.
   *
   * Carried on the instance rather than recomputed by each consumer because
   * every consumer got it wrong the same way: they sized their box by
   * `canopyRadiusXZ`, which is the *central* mass alone, and sliced the lobes
   * off flat wherever they overhung it.
   */
  readonly reach: number;
  readonly topY: number;
  /** Horizontal drift of the trunk per voxel of height — the tree's lean. */
  readonly leanX: number;
  readonly leanZ: number;
  /** Branches, each rising from a fraction of the trunk out to a crown lobe. */
  readonly branches: readonly Branch[];
  /** Extra crown lobes, offset from the main mass, in voxels. */
  readonly lobes: readonly Lobe[];
  /** Per-tree salt so two trees in the same cell lattice never share lumps. */
  readonly salt: number;
}

/** A limb: leaves the trunk at `fromY` and reaches `to`. */
export interface Branch {
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
  readonly toZ: number;
  readonly radius: number;
}

export interface Lobe {
  readonly dx: number;
  readonly dy: number;
  readonly dz: number;
  readonly radius: number;
}

function treeCell(value: number): number {
  return Math.floor(value / TREE_CELL);
}

/**
 * The tree occupying a grid cell, or `null`. Depends only on the cell and the
 * terrain under it, so every sub-chunk that overlaps a canopy agrees on it.
 */
export function treeAt(
  seed: number,
  graph: RegionGraph,
  cellX: number,
  cellZ: number,
  biome: BiomeId,
): TreeInstance | null {
  const density = TREE_DENSITY[biome] ?? 0;
  if (density <= 0) return null;
  if (hashFloat(seed ^ 0x2be7_11ca, cellX, 0, cellZ) >= density) return null;
  const jitter = TREE_CELL * 0.36;
  const x = Math.round(cellX * TREE_CELL + TREE_CELL / 2 + hashSigned(seed ^ 0x51a3_7c0d, cellX, 0, cellZ) * jitter);
  const z = Math.round(cellZ * TREE_CELL + TREE_CELL / 2 + hashSigned(seed ^ 0x7d17_9b41, cellX, 0, cellZ) * jitter);
  // The eroded surface, not the smooth field: the terrain fill displaces each
  // column by a couple of voxels, and a trunk rooted at the smooth height would
  // float over a hollow or be buried by a mound.
  const groundY = erodedSurfaceHeight(seed, x, z, graph);
  // Nothing takes root below the waterline or on the perimeter cliff.
  if (groundY <= SEA_LEVEL + voxels(0.5) || groundY >= WORLD_HEIGHT - MAX_VEGETATION_HEIGHT - 2) return null;
  const trunkRadius = TRUNK_RADIUS_MIN + Math.floor(hashFloat(seed ^ 0x6f0b_2d95, cellX, 1, cellZ) * (TRUNK_RADIUS_SPAN + 1));
  const trunkHeight = TRUNK_HEIGHT_MIN + Math.floor(hashFloat(seed ^ 0x1c9e_4477, cellX, 2, cellZ) * TRUNK_HEIGHT_SPAN);
  const canopyRadiusXZ = CANOPY_RADIUS_MIN + Math.floor(hashFloat(seed ^ 0x39b5_08e2, cellX, 3, cellZ) * CANOPY_RADIUS_SPAN);
  const salt = hash32(seed ^ 0x4d5a_1e63, cellX, 0, cellZ);

  // Lean. A perfectly vertical trunk is the single loudest tell that a tree was
  // generated rather than grown; every trunk in the references drifts.
  const leanX = hashSigned(seed ^ 0x7f4a_2b19, cellX, 4, cellZ) * 0.16;
  const leanZ = hashSigned(seed ^ 0x1d8c_63f5, cellX, 5, cellZ) * 0.16;

  const canopyRadiusY = Math.round(canopyRadiusXZ * 0.78);
  const canopyCenterY = groundY + trunkHeight;

  // Limbs out to the crown. Without them the canopy floats on a pole and the
  // silhouette has a hole where the branches should be.
  const branchCount = 2 + (hash32(seed ^ 0x3ac1_77b9, cellX, 6, cellZ) % 3);
  const branches: Branch[] = [];
  for (let index = 0; index < branchCount; index += 1) {
    const angle = hashFloat(seed ^ 0x5f2b_11d7, cellX, 10 + index, cellZ) * Math.PI * 2;
    const fromFraction = 0.5 + hashFloat(seed ^ 0x2ea7_9c31, cellX, 20 + index, cellZ) * 0.38;
    const offset = canopyRadiusXZ
      * (LOBE_OFFSET_MIN + hashFloat(seed ^ 0x6b19_4d0f, cellX, 30 + index, cellZ) * LOBE_OFFSET_SPAN);
    const fromY = groundY + Math.round(trunkHeight * fromFraction);
    branches.push({
      fromY,
      toX: x + Math.round(Math.cos(angle) * offset) + Math.round(leanX * (canopyCenterY - fromY)),
      toY: fromY + Math.round((canopyCenterY - fromY) * (0.55 + hashFloat(seed ^ 0x11c7_3e85, cellX, 40 + index, cellZ) * 0.7)),
      toZ: z + Math.round(Math.sin(angle) * offset) + Math.round(leanZ * (canopyCenterY - fromY)),
      radius: Math.max(1, Math.round(trunkRadius * 0.55)),
    });
  }

  // Crown lobes, roughly one per branch, so the mass is a cluster rather than a
  // single ball with noise on it.
  const lobes: Lobe[] = branches.map((branch, index) => ({
    dx: branch.toX - x,
    dy: branch.toY - canopyCenterY,
    dz: branch.toZ - z,
    radius: Math.round(canopyRadiusXZ
      * (LOBE_RADIUS_MIN + hashFloat(seed ^ 0x48f2_7a6d, cellX, 50 + index, cellZ) * LOBE_RADIUS_SPAN)),
  }));

  // The crown's true envelope, measured over what was actually generated rather
  // than assumed from the nominal radius. `canopyOccupied` drifts the whole
  // crown sideways by the lean at each height, so the lean over the full rise to
  // the crown top counts toward the horizontal reach as well.
  const leanDrift = Math.max(Math.abs(leanX), Math.abs(leanZ)) * (trunkHeight + canopyRadiusY);
  let reach = canopyRadiusXZ;
  let topY = canopyCenterY + canopyRadiusY;
  for (const lobe of lobes) {
    reach = Math.max(reach, Math.hypot(lobe.dx, lobe.dz) + lobe.radius);
    topY = Math.max(topY, canopyCenterY + lobe.dy + Math.max(1, Math.round(lobe.radius * 0.8)));
  }

  return {
    reach: Math.ceil(reach + leanDrift) + 1,
    topY,
    x,
    z,
    groundY,
    trunkRadius,
    trunkHeight,
    canopyRadiusXZ,
    canopyRadiusY,
    canopyCenterY,
    leanX,
    leanZ,
    branches,
    lobes,
    salt,
  };
}

/** Every tree whose trunk or canopy can reach the given world box. */
export function treesNear(
  seed: number,
  graph: RegionGraph,
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
  biomeAt: (x: number, z: number) => BiomeId,
): readonly TreeInstance[] {
  const trees: TreeInstance[] = [];
  const firstCellX = treeCell(minX - MAX_CANOPY_REACH);
  const lastCellX = treeCell(maxX + MAX_CANOPY_REACH);
  const firstCellZ = treeCell(minZ - MAX_CANOPY_REACH);
  const lastCellZ = treeCell(maxZ + MAX_CANOPY_REACH);
  for (let cellZ = firstCellZ; cellZ <= lastCellZ; cellZ += 1) {
    for (let cellX = firstCellX; cellX <= lastCellX; cellX += 1) {
      const centerX = cellX * TREE_CELL + TREE_CELL / 2;
      const centerZ = cellZ * TREE_CELL + TREE_CELL / 2;
      const tree = treeAt(seed, graph, cellX, cellZ, biomeAt(centerX, centerZ));
      if (tree) trees.push(tree);
    }
  }
  return trees;
}

/**
 * Lumpy canopy occupancy. A cheap 4-voxel-cell hash rather than value noise:
 * a canopy bounding box is ~150 000 voxels and this runs for every one of
 * them, so a single integer avalanche is the whole budget.
 */
/** Squared distance from a point to a segment, for branch capsules. */
function distanceToSegmentSquared(
  px: number, py: number, pz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const abz = bz - az;
  const lengthSquared = abx * abx + aby * aby + abz * abz;
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1,
    ((px - ax) * abx + (py - ay) * aby + (pz - az) * abz) / lengthSquared));
  const dx = px - (ax + abx * t);
  const dy = py - (ay + aby * t);
  const dz = pz - (az + abz * t);
  return dx * dx + dy * dy + dz * dz;
}

/**
 * Trunk and limbs. The trunk leans as it rises and tapers toward the crown; the
 * branches are capsules from the trunk out to each crown lobe.
 */
function trunkOccupied(tree: TreeInstance, x: number, y: number, z: number, trunkTop: number): boolean {
  if (y >= tree.groundY && y <= trunkTop) {
    const rise = y - tree.groundY;
    const centreX = tree.x + tree.leanX * rise;
    const centreZ = tree.z + tree.leanZ * rise;
    // Taper: a trunk that keeps its full width to the crown reads as a pillar.
    const taper = 1 - 0.35 * (rise / Math.max(1, tree.trunkHeight));
    const radius = Math.max(1, tree.trunkRadius * taper);
    const dx = x - centreX;
    const dz = z - centreZ;
    if (dx * dx + dz * dz <= radius * radius) return true;
  }
  for (const branch of tree.branches) {
    const rise = branch.fromY - tree.groundY;
    const fromX = tree.x + tree.leanX * rise;
    const fromZ = tree.z + tree.leanZ * rise;
    const radius = branch.radius;
    if (distanceToSegmentSquared(x, y, z, fromX, branch.fromY, fromZ, branch.toX, branch.toY, branch.toZ)
      <= radius * radius) return true;
  }
  return false;
}

function canopyOccupied(tree: TreeInstance, x: number, y: number, z: number): boolean {
  // The crown is a cluster: a central mass plus a lobe at the end of each
  // branch. One ellipsoid, however noisy, always reads as a ball on a stick.
  const leanShift = (y - tree.groundY);
  const centreX = tree.x + tree.leanX * leanShift;
  const centreZ = tree.z + tree.leanZ * leanShift;
  let envelope = Number.POSITIVE_INFINITY;
  {
    const dx = (x - centreX) / tree.canopyRadiusXZ;
    const dy = (y - tree.canopyCenterY) / tree.canopyRadiusY;
    const dz = (z - centreZ) / tree.canopyRadiusXZ;
    envelope = dx * dx + dy * dy + dz * dz;
  }
  for (const lobe of tree.lobes) {
    const dx = (x - (centreX + lobe.dx)) / lobe.radius;
    const dy = (y - (tree.canopyCenterY + lobe.dy)) / Math.max(1, Math.round(lobe.radius * 0.8));
    const dz = (z - (centreZ + lobe.dz)) / lobe.radius;
    envelope = Math.min(envelope, dx * dx + dy * dy + dz * dz);
  }

  // A crown is not an ellipsoid. Distorting the envelope by a coarse lump makes
  // the silhouette irregular at branch scale — roughly a metre — which is what
  // separates a tree from a lollipop. Only the rim was ragged before, so every
  // crown read as a smooth ball with a fuzzy edge.
  const coarse = hashFloat(tree.salt, x >> 4, y >> 4, z >> 4);
  const branch = hashFloat(tree.salt ^ 0x51a3_7c0d, x >> 3, y >> 3, z >> 3);
  const distorted = envelope * (0.72 + coarse * 0.34 + branch * 0.28);
  if (distorted > 1) return false;

  // Holes right through the crown. The references show sky and backlight
  // between the leaf clumps; a solid mass reads as moulded plastic and also
  // costs nothing to render, since greedy meshing drops every interior face.
  const gap = hashFloat(tree.salt ^ 0x2be7_11ca, x >> 2, y >> 2, z >> 2);
  if (gap < 0.14) return false;

  // Fine speckle only near the rim, so the outer surface stays broken up.
  if (distorted < 0.55) return true;
  const speckle = hashFloat(tree.salt ^ 0x9e37_79b9, x >> 1, y >> 1, z >> 1);
  return speckle > (distorted - 0.55) / 0.62;
}

export interface VegetationRequest {
  readonly seed: number;
  readonly graph: RegionGraph;
  /** World coordinate of lattice index 0 on each axis. */
  readonly minX: number;
  readonly minY: number;
  readonly minZ: number;
  /** LOD stride in voxels. */
  readonly step: number;
  /** Smooth surface height per column, x-major, CHUNK_SIZE². */
  readonly surfaces: Int16Array;
  /** Biome per column, x-major, CHUNK_SIZE². */
  readonly biomes: readonly BiomeId[];
  readonly trees: readonly TreeInstance[];
  /** Writes one voxel; the caller owns bounds and protection rules. */
  place(ix: number, iy: number, iz: number, block: number): void;
}

/**
 * Solid trunk and canopy voxels. Leaves are *opaque* flat-coloured cubes, not
 * alpha cutouts: with no textures there is no alpha to cut, and opacity lets
 * greedy meshing drop every interior face of a 100 000-voxel crown.
 */
export function stampTrees(request: VegetationRequest, size: number): void {
  const { minX, minY, minZ, step, trees, place } = request;
  const maxY = minY + (size - 1) * step;
  const first = (min: number, world: number): number => Math.max(0, Math.ceil((world - min) / step));
  const last = (min: number, world: number): number => Math.min(size - 1, Math.floor((world - min) / step));
  for (const tree of trees) {
    if (tree.topY < minY || tree.groundY > maxY) continue;
    // The crown's own reach, which includes its lobes and its lean. Sizing this
    // box by `canopyRadiusXZ` cut every overhanging lobe off in a flat plane.
    const reach = Math.max(tree.reach, tree.trunkRadius);
    const firstX = first(minX, tree.x - reach);
    const lastX = last(minX, tree.x + reach);
    const firstZ = first(minZ, tree.z - reach);
    const lastZ = last(minZ, tree.z + reach);
    const firstY = first(minY, tree.groundY);
    const lastY = last(minY, tree.topY);
    const trunkTop = tree.groundY + tree.trunkHeight;
    for (let iy = firstY; iy <= lastY; iy += 1) {
      const y = minY + iy * step;
      for (let iz = firstZ; iz <= lastZ; iz += 1) {
        const z = minZ + iz * step;
        for (let ix = firstX; ix <= lastX; ix += 1) {
          const x = minX + ix * step;
          if (trunkOccupied(tree, x, y, z, trunkTop)) {
            place(ix, iy, iz, WORLDGEN_BLOCK_ID.oakLog);
            continue;
          }
          if (canopyOccupied(tree, x, y, z)) place(ix, iy, iz, WORLDGEN_BLOCK_ID.oakLeaves);
        }
      }
    }
  }
}

/**
 * Grass blades and flowers on one column. Blades are individual 1–2 voxel wide
 * towers, 4–12 voxels tall — geometry you walk *into*, never a texture on a
 * flat top face (docs/ART-DIRECTION.md §1.4).
 */
export function groundCoverHeight(seed: number, x: number, z: number, biome: BiomeId): number {
  const density = GROUND_COVER_DENSITY[biome] ?? 0;
  if (density <= 0) return 0;
  /*
   * Tall grass grows in *clumps*, not as a uniform pelt.
   *
   * The references are mostly plain grass surface with tufts gathered in
   * patches, plus bare rock and dirt. Thinning a uniform field per column, as
   * this did, gives an even stubble everywhere — the whole ground reads as one
   * material and there is nothing for the tufts to stand out against.
   *
   * So: a broad patch field gates whole areas out entirely, and only inside a
   * patch does the per-column draw apply.
   */
  const patch = hashFloat(seed ^ 0x0d3a_55c1, x >> 4, 0, z >> 4);
  if (patch < GROUND_COVER_PATCH_GATE) return 0;
  const patchStrength = Math.min(1, (patch - GROUND_COVER_PATCH_GATE) / GROUND_COVER_PATCH_RAMP);
  if (hashFloat(seed ^ 0x5bd1_e995, x, 0, z) >= density * patchStrength) return 0;
  // Per-column jitter dominates the patch clump: neighbouring blades of equal
  // height merge into one quad and the field flattens into slabs. Varying
  // height per column is what keeps them reading as separate blades.
  //
  // Weighted toward the short end rather than spread evenly. A uniform draw over
  // 2-8 voxels puts the average blade at 60 cm, which is thigh-high on the player
  // and closes the ground off; the references are a short field with taller tufts
  // gathered in it, so most of the draw wants to sit low and the tall end wants to
  // stay occasional.
  const clump = hashFloat(seed ^ 0x27d4_eb2f, x >> 3, 0, z >> 3);
  const jitter = hashFloat(seed ^ 0x1656_67b1, x, 1, z);
  const draw = (clump * 0.3 + jitter * 0.7) ** 1.7;
  return BLADE_HEIGHT_MIN + Math.floor(draw * BLADE_HEIGHT_SPAN);
}

const FLOWER_BLOCKS = [
  WORLDGEN_BLOCK_ID.flowerViolet,
  WORLDGEN_BLOCK_ID.flowerRed,
  WORLDGEN_BLOCK_ID.flowerOrange,
] as const;

/**
 * Voxels of bloom at the tip of a flowering blade, or 0 if it is too short to
 * carry one and still show a stem.
 *
 * One voxel is 12.5 cm, which is smaller than a pixel at any distance you would
 * actually see a meadow from — a single-voxel flower read as a stuck pixel, and
 * a scatter of them read as sensor noise rather than as flowers. The references
 * show blooms as small clusters on a visible stem, so the head takes the top two
 * or three voxels of the blade and leaves the rest green.
 */
export function flowerHeadHeight(seed: number, x: number, z: number, bladeHeight: number): number {
  if (bladeHeight <= 1) return 0;
  const head = 2 + (hash32(seed ^ 0x1f83_d9ab, x, 4, z) % 2);
  return Math.min(head, bladeHeight - 1);
}

/** Sparse accents, per docs/ART-DIRECTION.md §2 — used sparingly, never fields. */
export function flowerAt(seed: number, x: number, z: number, biome: BiomeId): number | null {
  if ((GROUND_COVER_DENSITY[biome] ?? 0) <= 0) return null;
  const patch = hashFloat(seed ^ 0x77b5_2d31, x >> 6, 0, z >> 6);
  if (patch < 0.72) return null;
  // Thinned from 0.09 to hold the *screen area* of flowers steady now that a
  // bloom is two or three voxels rather than one and the grass it grows in is
  // three times denser. Both changes multiply how much of a meadow is flower, and
  // together they took it from a scatter of accents to a wildflower field.
  if (hashFloat(seed ^ 0x3f4a_9c17, x, 2, z) >= 0.045) return null;
  return FLOWER_BLOCKS[hash32(seed ^ 0x62a1_0e5f, x, 3, z) % FLOWER_BLOCKS.length] ?? null;
}

/**
 * Loose rock lying on the ground.
 *
 * The references scatter boulders across grass — a grey mass sitting in the
 * green (`vibeshot 01-53-29`). They matter out of proportion to their size:
 * they are the only thing breaking a meadow's colour, and without them a field
 * is one hue from edge to edge however well the greens are varied.
 */
const BOULDER_CELL = voxels(11);
const BOULDER_RADIUS_MIN = voxels(0.35);
const BOULDER_RADIUS_SPAN = voxels(0.55);

export const MAX_BOULDER_REACH = BOULDER_RADIUS_MIN + BOULDER_RADIUS_SPAN;

export interface BoulderInstance {
  readonly x: number;
  readonly z: number;
  readonly groundY: number;
  readonly radiusXZ: number;
  readonly radiusY: number;
  readonly salt: number;
}

function boulderCell(value: number): number {
  return Math.floor(value / BOULDER_CELL);
}

/** Boulders whose mass can reach the given world box. */
export function bouldersNear(
  seed: number,
  graph: RegionGraph,
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
): readonly BoulderInstance[] {
  const boulders: BoulderInstance[] = [];
  const firstCellX = boulderCell(minX - MAX_BOULDER_REACH);
  const lastCellX = boulderCell(maxX + MAX_BOULDER_REACH);
  const firstCellZ = boulderCell(minZ - MAX_BOULDER_REACH);
  const lastCellZ = boulderCell(maxZ + MAX_BOULDER_REACH);
  for (let cellZ = firstCellZ; cellZ <= lastCellZ; cellZ += 1) {
    for (let cellX = firstCellX; cellX <= lastCellX; cellX += 1) {
      if (hashFloat(seed ^ 0x24b1_9f07, cellX, 7, cellZ) > 0.34) continue;
      const jitter = BOULDER_CELL * 0.4;
      const x = Math.round(cellX * BOULDER_CELL + BOULDER_CELL / 2
        + hashSigned(seed ^ 0x7c2e_5a19, cellX, 8, cellZ) * jitter);
      const z = Math.round(cellZ * BOULDER_CELL + BOULDER_CELL / 2
        + hashSigned(seed ^ 0x3d90_c14b, cellX, 9, cellZ) * jitter);
      const groundY = erodedSurfaceHeight(seed, x, z, graph);
      if (groundY <= SEA_LEVEL + voxels(0.5)) continue;
      const radiusXZ = BOULDER_RADIUS_MIN
        + Math.round(hashFloat(seed ^ 0x18ff_2a63, cellX, 10, cellZ) * BOULDER_RADIUS_SPAN);
      boulders.push({
        x,
        z,
        groundY,
        radiusXZ,
        // Squashed, so it sits in the ground rather than balancing on it.
        radiusY: Math.max(1, Math.round(radiusXZ * 0.62)),
        salt: hash32(seed ^ 0x6ea3_1d55, cellX, 0, cellZ),
      });
    }
  }
  return boulders;
}

/** True when the boulder's lumpy mass covers a voxel. */
export function boulderOccupies(boulder: BoulderInstance, x: number, y: number, z: number): boolean {
  const dx = (x - boulder.x) / boulder.radiusXZ;
  const dy = (y - boulder.groundY) / boulder.radiusY;
  const dz = (z - boulder.z) / boulder.radiusXZ;
  const envelope = dx * dx + dy * dy + dz * dz;
  if (envelope > 1.15) return false;
  if (envelope < 0.5) return true;
  // Chipped rather than spherical; a smooth ellipsoid of stone reads as an egg.
  const lump = hashFloat(boulder.salt, x >> 1, y >> 1, z >> 1);
  return lump > (envelope - 0.5) / 0.65;
}
